import { useBookingEngineStateContext, usePropertyContext } from "@app/app/hooks";
import { IBookingEngineState } from "@app/app/types";
import { ControlledFormField } from "@app/common/components/form/ControlledFormField";
import { CurrencySelect } from "@app/common/components/form/CurrencySelect";
import { Label } from "@app/common/components/form/Label";
import { LanguageSelect } from "@app/common/components/form/LanguageSelect";
import { Badge } from "@app/componentLibrary/ui/badge";
import { Button } from "@app/componentLibrary/ui/button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogHeader,
    DialogHeightModes,
    DialogStyleModes,
    DialogTitle,
    DialogTrigger,
    DialogVerticalPositions,
} from "@app/componentLibrary/ui/dialog";
import { Flex } from "@app/componentLibrary/ui/flex";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@app/componentLibrary/ui/tooltip";
import { GlobeAltIcon } from "@heroicons/react/20/solid";
import { TooltipArrow } from "@radix-ui/react-tooltip";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

const fallbackCurrency = { id: "fallback-currency", label: "EUR", code: "EUR", symbol: "€" };

// TODO: this is a temmporary solution as requested urgently, this will be set as a configuration option in the future
// Below is the list of Cross properties that should have only the default currency shown
const propertyIdsWithOnlyDefaultCurrency = [
    "9e4c1fbe-df3f-4220-93e6-c3ee842dd296", // Away Bali Legian Camakila
    "9e51b104-9b92-4605-9719-aef16efcaa3f", // Cross Bali Breakers Resort
    "9d41f698-b9a1-4dd9-9d71-f4991729ea8f", // Cross Paasha Bali Seminyak
    "9d42933b-01f4-4334-a95b-c0a6481aba3d", // Cross Vibe Paasha Atelier Bali Kuta
    "9e4f906a-1440-470c-ad2a-69a91e0fefa3", // Tanadewa Resort Ubud Bali By Cross Collection
    "9e4e5651-6d63-4462-999a-144b8272aed3", // Tanadewa Villa Nusa Dua Bali By Cross Collection
];

export const LanguageAndCurrencySelector = () => {
    const { activeProperty, propertyGroup } = usePropertyContext();
    const { control, setValue } = useFormContext<IBookingEngineState>();
    const { getState } = useBookingEngineStateContext();
    const { language, currency } = getState();
    const { t } = useTranslation("common");
    const propertyLanguages = activeProperty?.languages || propertyGroup?.fallback_languages || [];
    const propertyCurrencies = activeProperty?.currencies || propertyGroup?.fallback_currencies || [];
    const defaultCurrency = activeProperty?.settings.default_currency || fallbackCurrency;
    const quickSelectLanguages = [...propertyLanguages].slice(0, 4);
    // TODO: temp solution, this logic will be based on property settings in the future
    const keepOnlyDefaultCurrency = propertyIdsWithOnlyDefaultCurrency.includes(activeProperty?.id || "");
    const filteredCurrencies = propertyCurrencies.filter((curr) => (keepOnlyDefaultCurrency ? curr.code === defaultCurrency.code : true));

    const changeLanguage = (lang: string) => {
        setValue("language", lang);
    };

    const changeCurrency = (curr: string) => {
        setValue("currency", curr);
    };

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger>
                    <Dialog>
                        <DialogTrigger asChild>
                            <Button data-testid="global-icon-btn" variant="ghost" size="icon" className="text-gray-500">
                                <GlobeAltIcon width={20} />
                            </Button>
                        </DialogTrigger>
                        <DialogContent
                            verticalPosition={DialogVerticalPositions.Bottom}
                            heightMode={DialogHeightModes.Fit}
                            styleMode={DialogStyleModes.Regular}
                        >
                            <DialogHeader>
                                <DialogTitle>{t("select")}</DialogTitle>
                            </DialogHeader>
                            <div className="w-full p-4 overflow-x-scroll">
                                <div data-testid="language-select-field" className="mx-auto mt-3 text-left">
                                    <Label>{t("chooseLanguage")}</Label>
                                    <ControlledFormField
                                        name="language"
                                        control={control}
                                        component={LanguageSelect}
                                        languages={propertyLanguages}
                                    />
                                </div>
                                <Flex data-testid="default-languages" justifyContent="start" className="gap-x-2 mt-3">
                                    {quickSelectLanguages.map((lang) => (
                                        <Badge
                                            key={lang.code}
                                            variant={language === lang.code ? "secondary" : "outline"}
                                            className="cursor-pointer p-3"
                                            onClick={() => changeLanguage(lang.code)}
                                        >
                                            {lang.label}
                                        </Badge>
                                    ))}
                                </Flex>

                                <div data-testid="currency-select-field" className="mx-auto mt-8  text-left">
                                    <Label>{t("chooseCurrency")}</Label>
                                    <ControlledFormField
                                        name="currency"
                                        control={control}
                                        component={CurrencySelect}
                                        currencies={filteredCurrencies}
                                    />
                                </div>
                                <Flex data-testid="default-currencies" justifyContent="start" className="gap-x-1 mt-3">
                                    {[defaultCurrency].map((cur, curIndex) => (
                                        <Badge
                                            data-testid={`currency-item-${curIndex}`}
                                            color={currency === cur.code ? "primary" : "slate"}
                                            className="cursor-pointer p-3"
                                            key={cur.code}
                                            variant={currency === cur.code ? "secondary" : "outline"}
                                            onClick={() => changeCurrency(cur.code || "")}
                                        >
                                            {`${cur.symbol} ${cur.code}`}
                                        </Badge>
                                    ))}
                                </Flex>

                                <DialogClose asChild>
                                    <Button data-testid="save-preference-btn" className="w-full mt-6">
                                        {t("save")}
                                    </Button>
                                </DialogClose>
                            </div>
                        </DialogContent>
                    </Dialog>
                </TooltipTrigger>
                <TooltipContent sideOffset={2}>
                    <p className="text-xs">Language & Currency</p>
                    <TooltipArrow className="TooltipArrow" />
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};
